import { graphql } from 'react-relay';
import type { DeepNonNullable, DeepWritable } from 'ts-essentials';
import { genericMutation } from '../utils';
import type { Category_Create_Mutation } from './__generated__/Category_Create_Mutation.graphql';
import type { Category_Delete_Mutation } from './__generated__/Category_Delete_Mutation.graphql';
import type {
  Category_UpdatePinnedContent_Mutation,
  Category_UpdatePinnedContent_Mutation$rawResponse,
} from './__generated__/Category_UpdatePinnedContent_Mutation.graphql';
import type { Category_Update_Mutation } from './__generated__/Category_Update_Mutation.graphql';

const CreateCategoryMutation = graphql`
  mutation Category_Create_Mutation($input: CreateCategoryInput!, $connections: [ID!]!) {
    createCategory(input: $input) {
      category @appendNode(connections: $connections, edgeTypeName: "Category") {
        __id
        id
        name
        count
        children(first: 1000) {
          __id
          edges {
            cursor
            node {
              id
              name
              count
              znglycategory {
                uxOrder
                pinnedContent {
                  ... on ZnglyVideo {
                    id
                    title
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const createCategoryMutation = genericMutation<Category_Create_Mutation>({
  mutation: CreateCategoryMutation,
});

const DeleteCategoryMutation = graphql`
  mutation Category_Delete_Mutation($input: DeleteCategoryInput!) @raw_response_type {
    deleteCategory(input: $input)  {
      category {
        id @deleteRecord
      }
    }
  }
`;

export const deleteCategoryMutation = genericMutation<Category_Delete_Mutation>(({ input }) => ({
  mutation: DeleteCategoryMutation,
  optimisticResponse: {
    deleteCategory: {
      category: {
        id: input.id,
      },
    },
  },
}));

const UpdateCategoryMutation = graphql`
  mutation Category_Update_Mutation($input: UpdateCategoryInput!) @raw_response_type {
    updateCategory(input: $input) {
      category {
        id
        name
        znglycategory {
          uxOrder
        }
      }
    }
  }
`;

export const updateCategoryMutation = genericMutation<Category_Update_Mutation>(({ input }) => ({
  mutation: UpdateCategoryMutation,
  optimisticResponse: {
    updateCategory: {
      category: {
        id: input.id,
        name: input.name,
        znglycategory: {
          uxOrder: input.uxOrder,
        },
      },
    },
  },
}));

const UpdateCategoryPinnedContentMutation = graphql`
  mutation Category_UpdatePinnedContent_Mutation($input: UpdateCategoryInput!) @raw_response_type {
    updateCategory(input: $input) {
      category {
        id
        znglycategory {
          pinnedContent {
            ... on ZnglyVideo {
              id
            }
          }
        }
      }
    }
  }
`;

export const updateCategoryPinnedContentMutation = genericMutation<Category_UpdatePinnedContent_Mutation>(
  ({ input }) => {
    type PinnedType = DeepWritable<
      DeepNonNullable<
        Category_UpdatePinnedContent_Mutation$rawResponse['updateCategory']
      >['category']['znglycategory']['pinnedContent']
    >;

    const pinnedContent = input?.pinnedContent?.length
      ? input?.pinnedContent?.reduce<PinnedType>((acc, id) => {
          if (typeof id === 'string') {
            acc.push({
              __typename: 'ZnglyVideo',
              __isNode: 'ZnglyVideo',
              id,
            });
          }
          return acc;
        }, [])
      : null;

    return {
      mutation: UpdateCategoryPinnedContentMutation,
      optimisticResponse: {
        updateCategory: {
          category: {
            id: input.id,
            znglycategory: {
              pinnedContent: pinnedContent,
            },
          },
        },
      },
    };
  }
);
